import styled, { css, keyframes } from 'styled-components';

import { Text } from '../../../../components';
import {
  COLORS,
  MOBILE,
  REDUCED_MOTION,
  SECURE_BRAND,
  SECURE_BRAND_ANIM,
  SPACE,
  STATUS_COLORS,
} from '../../../../theme/tokens';
import { DocusignLogo } from '../DocusignLogo';

/** Which step's bloom a picture well carries. */
export type StageTone = keyof typeof SECURE_BRAND.stageBloom;

/** The picture well's height: every card reserves it, so the three cards share their layout. */
const STAGE_PX = 156;
const STAGE_PX_MOBILE = 144;
/** Container widths (the row's, not the window's): three across from STACK_BELOW_PX up; stacked below it,
 *  each card a row (picture beside its words) down to SIDE_BY_SIDE_FROM_PX, a column under that. */
const STACK_BELOW_PX = 759;
const SIDE_BY_SIDE_FROM_PX = 480;
const SIDE_STAGE_PX = 240;

/** How far the steps that are done or waiting dim back while signing is lit. */
const DIM_OPACITY = 0.42;

/**
 * A card rises into place out of a soft blur, and a touch smaller than it lands. No `to`: it lands on
 * the card's own state, so the lit signing step rises straight into its lifted place.
 */
const cardRise = keyframes`
  from {
    opacity: 0;
    transform: translateY(${SECURE_BRAND_ANIM.CARD_RISE_PX}px) scale(0.985);
    filter: blur(${SECURE_BRAND_ANIM.CARD_BLUR_PX}px);
  }
`;

/** Step 1's check, popping in once the step is done. */
const donePop = keyframes`
  from {
    opacity: 0;
    transform: scale(0.4);
  }
  60% {
    opacity: 1;
    transform: scale(1.12);
  }
`;

/** A card's picture follows it in, rising a little further. */
const stageRise = keyframes`
  from {
    opacity: 0;
    transform: translateY(${SPACE[12]}) scale(0.97);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

/** The heading row and the cards. A size container: the row stacks on its own width, not the window's. */
export const Root = styled.div`
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
`;

/** "How it works" and, across from it, the badge. Wraps under it when there is no room. */
export const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACE[8]} ${SPACE[16]};
`;

export const Title = styled(Text)`
  margin: ${SPACE[0]};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  color: ${COLORS.textStrong};
`;

/** "Powered by" and DocuSign's logo, in a quiet pill. */
export const PoweredBy = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[8]};
  height: 28px;
  padding: ${SPACE[0]} ${SPACE[12]};
  border: 1px solid ${COLORS.hairline};
  border-radius: 99px;
  background: ${COLORS.surfaceFainter};
`;

export const PoweredByLabel = styled(Text)`
  font-size: 12px;
  line-height: 1;
  white-space: nowrap;
  color: ${COLORS.textMuted};
`;

/** The logo at the label's cap height, its wordmark in the page's strong ink. */
export const Logo = styled(DocusignLogo)`
  font-size: 13px;
  color: ${COLORS.textStrong};
`;

/** The steps, three across; one column once the row is too narrow for three readable cards. */
export const Cards = styled.ol`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${SPACE[12]};
  margin: ${SPACE[0]};
  padding: ${SPACE[0]};
  list-style: none;
  counter-reset: secure-brand-step;

  /* The row's own width (Root is the container), not the window's: three across need about 240px a
     card (the email picture is 196px wide), so a narrower row stacks them. */
  @container (max-width: ${STACK_BELOW_PX}px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

/**
 * One step: the picture well on top, the step's words under it. Enters on its own turn.
 * `data-emphasis` (Document sent): `dim` for the steps done or waiting, `spotlight` for signing.
 */
export const Card = styled.li<{ $delayMs: number }>`
  counter-increment: secure-brand-step;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[16]};
  min-width: 0;
  padding: ${SPACE[8]} ${SPACE[8]} ${SPACE[20]};
  border: 1px solid ${COLORS.hairline};
  border-radius: 16px;
  /* The area's card-in-a-container surface, darker than the panel it sits in. */
  background: ${COLORS.metricTileBg};
  animation: ${cardRise} ${SECURE_BRAND_ANIM.CARD_ENTER_MS}ms ${SECURE_BRAND_ANIM.EASE} backwards;
  animation-delay: ${(p) => p.$delayMs}ms;
  transition-property: transform, border-color, box-shadow;
  transition-duration: ${SECURE_BRAND_ANIM.SPOTLIGHT_MS}ms;
  transition-timing-function: ${SECURE_BRAND_ANIM.EASE};

  &[data-emphasis='spotlight'] {
    z-index: 1;
    transform: translateY(-${SECURE_BRAND_ANIM.SPOTLIGHT_LIFT_PX}px) scale(1.015);
    border-color: ${COLORS.ctaBorder};
    box-shadow: ${SECURE_BRAND.spotlightGlow};
  }

  /* Stacked on a mid-width row: the picture beside the step's words. */
  @container (min-width: ${SIDE_BY_SIDE_FROM_PX}px) and (max-width: ${STACK_BELOW_PX}px) {
    flex-direction: row;
    padding: ${SPACE[8]};
  }

  ${REDUCED_MOTION} {
    animation: none;
    transition: none;
  }
`;

/** What a dimmed step dims: its picture and its words, never its buttons. */
const dimmable = css`
  transition: opacity ${SECURE_BRAND_ANIM.SPOTLIGHT_MS}ms ${SECURE_BRAND_ANIM.EASE};

  [data-emphasis='dim'] & {
    opacity: ${DIM_OPACITY};
  }

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

/** The picture well: a step darker than the card, with its step's bloom rising from the bottom edge. */
export const Stage = styled.div<{ $tone: StageTone }>`
  position: relative;
  height: ${STAGE_PX}px;
  overflow: hidden;
  border-radius: 12px;
  background: ${(p) => SECURE_BRAND.stageBloom[p.$tone]}, ${SECURE_BRAND.stageBg};
  ${dimmable}

  ${MOBILE} {
    height: ${STAGE_PX_MOBILE}px;
  }

  @container (min-width: ${SIDE_BY_SIDE_FROM_PX}px) and (max-width: ${STACK_BELOW_PX}px) {
    flex: 0 0 ${SIDE_STAGE_PX}px;
    height: ${STAGE_PX}px;
  }
`;

/** Holds the picture centred in the well, and brings it in after its card. */
export const StageInner = styled.div<{ $delayMs: number }>`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  height: 100%;
  animation: ${stageRise} ${SECURE_BRAND_ANIM.STAGE_ENTER_MS}ms ${SECURE_BRAND_ANIM.EASE} backwards;
  animation-delay: ${(p) => p.$delayMs}ms;

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

/** The step's words, and step 1's button at the bottom. */
export const Body = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
  min-width: 0;
  padding: ${SPACE[0]} ${SPACE[12]};

  /* Beside the picture: as tall as the card, so step 1's Start ends level with the picture's bottom. */
  @container (min-width: ${SIDE_BY_SIDE_FROM_PX}px) and (max-width: ${STACK_BELOW_PX}px) {
    padding: ${SPACE[12]} ${SPACE[12]} ${SPACE[0]} ${SPACE[8]};
  }
`;

/** The step's badge beside its line. */
export const StepLine = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${SPACE[12]};
  ${dimmable}
`;

/**
 * The step's number (a CSS counter, so no digit is copy): `todo` plain, `current` filled in the brand
 * orange (the lit signing step), `done` a check on green in place of the number, popping in.
 */
export const StepBadge = styled.span<{ $state: 'todo' | 'current' | 'done' }>`
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  box-sizing: border-box;
  width: 24px;
  height: 24px;
  border-radius: 99px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceCard};
  font-size: 12px;
  font-weight: 700;
  color: ${COLORS.textMuted};
  transition-property: background-color, border-color, color;
  transition-duration: ${SECURE_BRAND_ANIM.SPOTLIGHT_MS}ms;
  transition-timing-function: ${SECURE_BRAND_ANIM.EASE};

  &::before {
    content: ${(p) => (p.$state === 'done' ? 'none' : 'counter(secure-brand-step)')};
  }

  ${(p) =>
    p.$state === 'current'
      ? css`
          border-color: ${COLORS.ctaSolid};
          background: ${COLORS.ctaSolid};
          color: ${COLORS.ctaSolidText};
        `
      : p.$state === 'done'
        ? css`
            border-color: transparent;
            background: ${COLORS.badgeGreenBg};
            color: ${STATUS_COLORS.positive};
            animation: ${donePop} ${SECURE_BRAND_ANIM.DONE_POP_MS}ms ${SECURE_BRAND_ANIM.EASE} backwards;
          `
        : null}

  ${REDUCED_MOTION} {
    animation: none;
    transition: none;
  }
`;

export const StepText = styled(Text)`
  min-width: 0;
  /* Off-scale on purpose: centres the first line of copy on the 24px step number. */
  padding-top: 2px;
  font-size: 14px;
  line-height: 20px;
  color: ${COLORS.text};
`;

/** Step 2's line for DocuSign's recognition check, under the step, aligned with its words. */
export const Note = styled(Text)`
  ${dimmable}
  margin: ${SPACE[0]};
  padding-left: ${SPACE[36]};
  font-size: 13px;
  line-height: 1.5;
  color: ${COLORS.textMuted};
`;

/** Step 1's Start, full width, and the line under it, pinned to the card's bottom. */
export const Action = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${SPACE[8]};
  margin-top: auto;
  padding-top: ${SPACE[4]};
`;
